import { useQueryClient } from "@tanstack/react-query";
import { createElement, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { refusalText } from "../api/errors";
import { useApiCommand } from "../api/hooks";
import type { DemoBannerBlock, ExplanationBlock, NoticeBlock, PointToBlock, Question, QuestionsBlock, SourceRef, TextBlock } from "../api/types";
import { pointToPath } from "../app/screenRoutes";
import { Banner } from "../ui/Banner";
import { Button } from "../ui/Button";
import { Pill } from "../ui/Pill";
import { SafeMarkdown } from "../ui/SafeMarkdown";
import { conversationQueryKey } from "./conversation";
import { useDraftStore } from "./draftStore";
import { NoteOfferBlockView } from "./NoteOfferBlock";
import { pointToBus } from "./pointTo";
import { blockType, getBlockRenderer, registerBlockRenderer, type BlockProps } from "./registry";

/** The agent's words, set in the serif type. Markdown is shown safely: no HTML, no links, no images. */
function AgentWords({ children }: { children: string }) {
  return (
    <div className="agent-text" data-agent="true">
      <SafeMarkdown>{children}</SafeMarkdown>
    </div>
  );
}

export function TextBlockView({ block, context }: BlockProps<TextBlock>) {
  // The person's own words are plain text in the screen's type, not the agent's serif.
  if (context.role === "user") return <p className="agent-user-text">{block.text}</p>;
  return <AgentWords>{block.text}</AgentWords>;
}

function QuestionInput({ question, busy, onAnswer }: { question: Question; busy: boolean; onAnswer: (answer: string) => void }) {
  const [text, setText] = useState("");
  const [picked, setPicked] = useState("");

  if (question.answer_type === "yes_no") {
    return (
      <div className="row">
        <Button disabled={busy} onClick={() => onAnswer("Yes")}>
          Yes
        </Button>
        <Button disabled={busy} onClick={() => onAnswer("No")}>
          No
        </Button>
      </div>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onAnswer(question.answer_type === "pick_one" ? picked : text);
  };

  if (question.answer_type === "pick_one") {
    return (
      <form onSubmit={submit} className="agent-question__pick">
        {question.options.map((option) => (
          <label key={option} className="agent-question__option">
            <input type="radio" name={question.text} value={option} checked={picked === option} onChange={() => setPicked(option)} /> {option}
          </label>
        ))}
        <Button type="submit" variant="primary" disabled={busy || !picked}>
          Answer
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={submit} className="row">
      <input
        type="text"
        className="field__input"
        aria-label={`Your answer to: ${question.text}`}
        value={text}
        onChange={(event) => setText(event.target.value)}
        disabled={busy}
      />
      <Button type="submit" variant="primary" disabled={busy || !text.trim()}>
        Answer
      </Button>
    </form>
  );
}

export function QuestionsBlockView({ block, context }: BlockProps<QuestionsBlock>) {
  const queryClient = useQueryClient();
  const answerQuestion = useApiCommand("answer_question");
  const answerAdminQuestion = useApiCommand("admin_answer_question");
  const [given, setGiven] = useState<Record<string, string>>({});
  const [busyIndex, setBusyIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const answers: Record<string, string> = { ...block.answers, ...given };

  const answer = async (index: number, raw: string) => {
    const text = raw.trim();
    if (!text) return;
    setBusyIndex(index);
    setError(null);
    try {
      const input = { key: context.conversationKey, block_id: block.block_id, question_index: index, answer: text };
      // The admin's conversations have their own path (the admin has no discipline).
      if (context.admin) await answerAdminQuestion.mutateAsync(input);
      else await answerQuestion.mutateAsync(input);
      setGiven((previous) => ({ ...previous, [String(index)]: text }));
      void queryClient.invalidateQueries({ queryKey: conversationQueryKey(context.conversationKey, context.admin) });
    } catch (problem) {
      setError(refusalText(problem));
    } finally {
      setBusyIndex(null);
    }
  };

  return (
    <section className="agent-questions" aria-label="Questions from the agent">
      {block.intro && <AgentWords>{block.intro}</AgentWords>}
      <ol className="agent-questions__list">
        {block.questions.map((question, index) => {
          const given = answers[String(index)];
          return (
            <li key={index} className="agent-question" data-answered={given !== undefined ? "true" : "false"}>
              <div className="agent-question__head">
                <Pill>{question.must_ask ? "Must ask" : "If relevant"}</Pill>
                <AgentWords>{question.text}</AgentWords>
              </div>
              {question.reason && <p className="agent-question__reason muted">Why I ask: {question.reason}</p>}
              {given !== undefined ? (
                <p className="agent-question__answer">
                  <span className="muted">Your answer:</span> {given}
                </p>
              ) : (
                <QuestionInput question={question} busy={busyIndex !== null} onAnswer={(text) => void answer(index, text)} />
              )}
            </li>
          );
        })}
      </ol>
      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}
    </section>
  );
}

const SOURCE_WORDS: Record<SourceRef["kind"], string> = {
  decision: "Decision",
  library_term: "Library term",
  element: "Element",
  spot: "Spot in a document",
  skill: "Guidance",
};

export function ExplanationBlockView({ block }: BlockProps<ExplanationBlock>) {
  return (
    <section className="agent-explanation" aria-label={`Explanation: ${block.subject}`}>
      <h3 className="agent-explanation__subject">{block.subject}</h3>
      <AgentWords>{block.text}</AgentWords>
      {block.sources.length > 0 && (
        <ul className="agent-sources" aria-label="Where this comes from">
          {block.sources.map((source) => {
            const label = source.label || source.ref;
            return (
              <li key={`${source.kind}:${source.ref}`}>
                <Pill>{SOURCE_WORDS[source.kind]}</Pill>{" "}
                {source.kind === "element" ? <Link to={`/elements/${encodeURIComponent(source.ref)}`}>{label}</Link> : <span>{label}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export function NoticeBlockView({ block }: BlockProps<NoticeBlock>) {
  return <Banner tone={block.tone === "warning" ? "warning" : "info"}>{block.text}</Banner>;
}

export function DemoBannerBlockView({ block }: BlockProps<DemoBannerBlock>) {
  return <Banner tone="demo">{block.text}</Banner>;
}

export function PointToBlockView({ block, context }: BlockProps<PointToBlock>) {
  const navigate = useNavigate();
  const to = pointToPath(block, { sessionId: context.sessionId });
  const show = () => {
    if (block.form_draft) useDraftStore.getState().set(block.form_draft);
    pointToBus.emit({ screen: block.screen, element_id: block.element_id, spot_id: block.spot_id, sentence: block.sentence, form_draft: block.form_draft });
    if (to) void navigate(to);
  };
  return (
    <div className="agent-point">
      <AgentWords>{block.sentence}</AgentWords>
      <Button onClick={show}>Show me</Button>
    </div>
  );
}

const PLAIN_NAMES: Record<string, string> = {
  plan: "plan",
  cards: "set of proposal cards",
  hand_over: "hand-over",
};

/** For a block no screen has registered a drawing for. */
export function UnknownBlockView({ block }: BlockProps) {
  const type = blockType(block);
  const name = PLAIN_NAMES[type];
  return (
    <p className="agent-unknown muted">
      {name ? `The agent shared a ${name} here. It is shown on the session screen.` : "The agent shared something this screen cannot show."}
    </p>
  );
}

/**
 * Draw one block through the registry. The component for a type is looked up in a table that screens fill
 * when they load, not made here, so it is the same component on every render.
 */
export function BlockView({ block, context }: BlockProps) {
  return createElement(getBlockRenderer(blockType(block)) ?? UnknownBlockView, { block, context });
}

/** F01's own blocks. The session screens register plan, cards and hand_over. The note offer is drawn in every panel. */
export function registerBuiltInBlocks(): void {
  registerBlockRenderer("text", TextBlockView);
  registerBlockRenderer("questions", QuestionsBlockView);
  registerBlockRenderer("explanation", ExplanationBlockView);
  registerBlockRenderer("notice", NoticeBlockView);
  registerBlockRenderer("demo_banner", DemoBannerBlockView);
  registerBlockRenderer("point_to", PointToBlockView);
  registerBlockRenderer("note_offer", NoteOfferBlockView);
}

registerBuiltInBlocks();
